import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import formatMoney from '../../core/formatMoney';
import type { YearlyTaxBreakdown } from '../hooks/useYearlyStatistics';

const headerCellSx = {
    fontWeight: 900,
    fontSize: '1.5rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    py: 2.5,
    px: 2.5,
    color: '#fff',
};

const dataCellSx = {
    py: 1.5,
    px: 2.5,
    borderBottom: '2px solid #374151',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
};

const valueCellSx = {
    ...dataCellSx,
    textAlign: 'right',
    fontWeight: 700,
    fontSize: '0.9375rem',
};

const categoryLabels: Record<string, string> = {
    indirect: 'Indirect Taxes',
    income: 'Income Taxes',
    social: 'Social Contributions',
    other: 'Other Taxes',
};

const BookingStats = ({
    taxBreakdown,
}: {
    taxBreakdown: YearlyTaxBreakdown;
}) => {
    const { year, currency, bookingsCount, daysHosted, grossRevenue, breakdown, totalsByCategory, totalTaxes, netIncome, effectiveRate, preset } = taxBreakdown;

    const finalPerDay = daysHosted > 0 ? netIncome / daysHosted : 0;

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                border: '4px solid #6366f1',
                borderRadius: 0,
                backgroundColor: '#0f0f23',
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow
                        sx={{
                            borderBottom: '4px solid #6366f1',
                            backgroundColor: '#6366f1',
                        }}
                    >
                        <TableCell colSpan={2} sx={headerCellSx}>
                            {year}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Preset Info */}
                    <TableRow>
                        <TableCell sx={dataCellSx}>Tax Profile</TableCell>
                        <TableCell sx={valueCellSx}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#f59e0b' }}>
                                {preset.name}
                            </Typography>
                        </TableCell>
                    </TableRow>

                    {/* Basic Stats */}
                    <TableRow>
                        <TableCell sx={dataCellSx}>Bookings</TableCell>
                        <TableCell sx={valueCellSx}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                                {bookingsCount}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={dataCellSx}>Days Hosted</TableCell>
                        <TableCell sx={valueCellSx}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                                {daysHosted}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={dataCellSx}>Gross Revenue</TableCell>
                        <TableCell sx={valueCellSx}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                                {formatMoney(grossRevenue, currency)}
                            </Typography>
                        </TableCell>
                    </TableRow>

                    {/* Tax Breakdown by Component */}
                    {breakdown.map((component) => (
                        <TableRow key={component.id}>
                            <TableCell sx={dataCellSx}>
                                {component.name}
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                        ml: 1,
                                    }}
                                >
                                    ({(component.rate * 100).toFixed(1)}%)
                                </Typography>
                            </TableCell>
                            <TableCell sx={valueCellSx}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                                    {formatMoney(component.amount, currency)}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}

                    {/* Category Subtotals */}
                    {Object.entries(totalsByCategory).map(([category, amount]) =>
                        amount > 0 ? (
                            <TableRow
                                key={category}
                                sx={{ backgroundColor: 'rgba(99, 102, 241, 0.08)' }}
                            >
                                <TableCell
                                    sx={{
                                        ...dataCellSx,
                                        fontWeight: 800,
                                        color: '#6366f1',
                                    }}
                                >
                                    {categoryLabels[category] ?? category}
                                </TableCell>
                                <TableCell sx={valueCellSx}>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: '0.9375rem',
                                            color: '#6366f1',
                                        }}
                                    >
                                        {formatMoney(amount, currency)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null
                    )}

                    {/* Total Taxes */}
                    <TableRow
                        sx={{
                            borderTop: '4px solid #ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        }}
                    >
                        <TableCell
                            sx={{
                                ...dataCellSx,
                                fontWeight: 900,
                                color: '#ef4444',
                                borderBottom: '2px solid #ef4444',
                            }}
                        >
                            Total Taxes
                        </TableCell>
                        <TableCell
                            sx={{
                                ...valueCellSx,
                                borderBottom: '2px solid #ef4444',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '0.9375rem',
                                    color: '#ef4444',
                                }}
                            >
                                {formatMoney(totalTaxes, currency)}
                            </Typography>
                        </TableCell>
                    </TableRow>

                    {/* Effective Rate */}
                    <TableRow>
                        <TableCell sx={dataCellSx}>Effective Tax Rate</TableCell>
                        <TableCell sx={valueCellSx}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                                {(effectiveRate * 100).toFixed(1)}%
                            </Typography>
                        </TableCell>
                    </TableRow>

                    {/* Net Income */}
                    <TableRow
                        sx={{
                            borderTop: '4px solid #22c55e',
                            backgroundColor: '#22c55e',
                        }}
                    >
                        <TableCell
                            sx={{
                                ...dataCellSx,
                                fontWeight: 900,
                                fontSize: '0.9375rem',
                                color: '#0f0f23',
                                borderBottom: 'none',
                            }}
                        >
                            Net Income
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, borderBottom: 'none' }}>
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1rem',
                                    color: '#0f0f23',
                                }}
                            >
                                {formatMoney(netIncome, currency)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow
                        sx={{
                            backgroundColor: 'rgba(34, 197, 94, 0.2)',
                        }}
                    >
                        <TableCell
                            sx={{
                                ...dataCellSx,
                                fontWeight: 900,
                                fontSize: '0.9375rem',
                                color: '#22c55e',
                                borderBottom: 'none',
                            }}
                        >
                            Net / Day
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, borderBottom: 'none' }}>
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1rem',
                                    color: '#22c55e',
                                }}
                            >
                                {formatMoney(finalPerDay, currency)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BookingStats;
